// The block menu, opened by clicking the block handle (drag-handle.ts sends an
// event with the block; the handle is plain DOM and cannot draw a menu).
//
// Promise:
// - The block is found again by its id right before an item runs: a remote edit
//   while the menu is open moves positions. A block inside a quote has no id and
//   is found by position (open for a few seconds; nothing else can reach it).
//   When the block is gone, nothing runs and the menu closes.
// - "Turn into" offers only the types `setNode` reaches (paragraph, headings,
//   code). Wrapping into a list or a quote gives the wrapper a new block id, and
//   a thread anchored to the block would come off; so it is not offered, and
//   neither is lifting a block out of a list or quote into a paragraph.
// - A quote or a list can be unwrapped (its content goes to the top level), and
//   a block inside a quote can be taken out of it: a block dropped into a quote
//   had no other way out.
// - "Copy as markdown" copies the block as the document would write it.
// - "Duplicate" inserts a copy without the block id (two blocks with one id
//   would be resolved by position, not by intent).
// - Closing returns the focus to the body without scrolling it (left without a
//   focus, region focus jumped to the first block at the top of the page).
import type { Editor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import {
  BLOCK_ID_ATTR,
  getEditorSchema,
  serializeMarkdown,
} from "@trameria/editor";
import { m } from "@trameria/i18n";
import { useEffect, useRef, useState } from "react";
import { toast } from "../../ui/toast";
import type { DocumentBlockPart } from "../blocks/block-parts";
import { FloatingMenu, type FloatingMenuItem } from "../blocks/floating-menu";
import { BLOCK_MENU_EVENT, type BlockMenuEventDetail } from "./drag-handle";

const NODE_TYPES = [
  {
    id: "paragraph",
    label: () => m.documents_block_paragraph(),
    name: "paragraph",
    attrs: {},
  },
  {
    id: "heading1",
    label: () => m.documents_block_heading1(),
    name: "heading",
    attrs: { level: 1 },
  },
  {
    id: "heading2",
    label: () => m.documents_block_heading2(),
    name: "heading",
    attrs: { level: 2 },
  },
  {
    id: "heading3",
    label: () => m.documents_block_heading3(),
    name: "heading",
    attrs: { level: 3 },
  },
  {
    id: "codeBlock",
    label: () => m.documents_block_code(),
    name: "codeBlock",
    attrs: {},
  },
] as const;

/** Containers whose content can stand at the top level as it is. */
const UNWRAP: Record<string, () => string> = {
  blockquote: () => m.documents_block_unwrap_quote(),
  bulletList: () => m.documents_block_unwrap_list(),
  orderedList: () => m.documents_block_unwrap_list(),
  taskList: () => m.documents_block_unwrap_list(),
};

interface Target {
  node: PMNode;
  pos: number;
}

/** A top-level block by id (the first in document order). */
export function findTopLevelBlock(doc: PMNode, blockId: string): Target | null {
  let found: Target | null = null;
  doc.forEach((child, offset) => {
    if (found === null && child.attrs[BLOCK_ID_ATTR] === blockId)
      found = { node: child, pos: offset };
  });
  return found;
}

function resolveTarget(
  editor: Editor,
  at: { blockId: string | null; pos: number },
): Target | null {
  if (at.blockId !== null)
    return findTopLevelBlock(editor.state.doc, at.blockId);
  if (!Number.isInteger(at.pos) || at.pos < 0) return null;
  const node = editor.state.doc.nodeAt(at.pos);
  return node ? { node, pos: at.pos } : null;
}

/** The markdown of one block, as the document writes it. */
export function blockMarkdown(node: PMNode): string {
  const doc = getEditorSchema("document").nodeFromJSON({
    type: "doc",
    content: [node.toJSON()],
  });
  return serializeMarkdown(doc).trim();
}

export interface BlockMenuItem {
  id: string;
  label: string;
  section?: string;
  danger?: boolean;
  run(editor: Editor, target: Target): void;
}

/**
 * The items for a block of a type (`nested`: inside a quote, no id). `own`:
 * a part's items for this block (block-parts.ts) go first; with
 * `replacesTypes` the "Turn into" items are left out (a ui block turned into
 * "code" would lose its language, and read like "show source").
 */
export function blockMenuItems(
  typeName: string,
  nested: boolean,
  own: { items: BlockMenuItem[]; replacesTypes?: boolean } | null = null,
): BlockMenuItem[] {
  const items: BlockMenuItem[] = [...(own?.items ?? [])];
  if (nested)
    items.push({
      id: "liftOut",
      label: m.documents_block_lift_out(),
      run: (editor, target) =>
        editor
          .chain()
          .focus()
          .command(({ tr, state }) => {
            const $pos = state.doc.resolve(target.pos);
            if ($pos.depth === 0) return false;
            // After the top-level container (the quote).
            const outerPos = $pos.before(1);
            const outer = state.doc.nodeAt(outerPos);
            if (!outer) return false;
            // deleteRange removes a quote left empty too.
            tr.deleteRange(target.pos, target.pos + target.node.nodeSize);
            tr.insert(tr.mapping.map(outerPos + outer.nodeSize), target.node);
            return true;
          })
          .run(),
    });
  const hasTypes =
    !nested &&
    own?.replacesTypes !== true &&
    NODE_TYPES.some((t) => t.name === typeName);
  if (hasTypes)
    for (const [i, t] of NODE_TYPES.entries())
      items.push({
        id: t.id,
        label: t.label(),
        section: i === 0 ? m.documents_block_menu_section_type() : undefined,
        run: (editor, target) =>
          editor
            .chain()
            .focus(undefined, { scrollIntoView: false })
            // Onto the block first: the handle's click does not move the
            // selection, and the caret's block would be turned instead.
            .setTextSelection(target.pos + 1)
            .setNode(t.name, { ...t.attrs })
            .run(),
      });
  // The second group's heading, only when there are two groups.
  let blockSection: string | undefined = hasTypes
    ? m.documents_block_menu_section_block()
    : undefined;
  const nextSection = () => {
    const s = blockSection;
    blockSection = undefined;
    return s;
  };
  const unwrap = nested ? undefined : UNWRAP[typeName];
  if (unwrap)
    items.push({
      id: "unwrap",
      label: unwrap(),
      section: nextSection(),
      run: (editor, target) =>
        editor
          .chain()
          .focus(undefined, { scrollIntoView: false })
          .command(({ tr }) => {
            // A list item cannot stand at the top level: take its content.
            const inner =
              target.node.type.name === "blockquote"
                ? target.node.content
                : target.node.child(0).content;
            if (inner.size === 0) return false;
            tr.replaceWith(
              target.pos,
              target.pos + target.node.nodeSize,
              inner,
            );
            return true;
          })
          .run(),
    });
  items.push({
    id: "copyMarkdown",
    label: m.documents_block_copy_markdown(),
    section: nextSection(),
    run: (_editor, target) => {
      const text = blockMarkdown(target.node);
      void navigator.clipboard
        ?.writeText(text)
        .then(() => toast(m.documents_block_copied()))
        .catch(() => toast(m.documents_block_copy_failed()));
    },
  });
  items.push({
    id: "duplicate",
    label: m.documents_block_duplicate(),
    run: (editor, target) => {
      const json = target.node.toJSON() as { attrs?: Record<string, unknown> };
      editor
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .insertContentAt(target.pos + target.node.nodeSize, {
          ...json,
          attrs: { ...json.attrs, [BLOCK_ID_ATTR]: null },
        })
        .run();
    },
  });
  items.push({
    id: "delete",
    label: m.documents_block_delete(),
    danger: true,
    run: (editor, target) =>
      editor
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .deleteRange({
          from: target.pos,
          to: target.pos + target.node.nodeSize,
        })
        .run(),
  });
  return items;
}

interface Open extends BlockMenuEventDetail {
  typeName: string;
  nested: boolean;
  /** A part's items for this block, read when the menu opens. */
  own: { items: BlockMenuItem[]; replacesTypes?: boolean } | null;
}

/** The first part that claims the block, with its items. */
function partItems(
  parts: readonly DocumentBlockPart[],
  editor: Editor,
  target: Target,
  nested: boolean,
): Open["own"] {
  for (const part of parts) {
    const own = part.blockMenu?.(editor, target, nested) ?? null;
    if (own) return own;
  }
  return null;
}

export function BlockMenu({
  editor,
  parts = [],
}: {
  editor: Editor;
  /** The document body's parts (block-parts.ts). */
  parts?: readonly DocumentBlockPart[];
}) {
  const [open, setOpen] = useState<Open | null>(null);
  const current = useRef<Open | null>(null);
  const latestParts = useRef(parts);
  latestParts.current = parts;

  // frontend-development: not a fetch; listens for the handle's click.
  useEffect(() => {
    const dom = editor.view.dom;
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<BlockMenuEventDetail>).detail;
      const target = resolveTarget(editor, detail);
      if (!target) {
        detail.close();
        return;
      }
      const nested = detail.blockId === null;
      const next = {
        ...detail,
        typeName: target.node.type.name,
        nested,
        own: partItems(latestParts.current, editor, target, nested),
      };
      current.current = next;
      setOpen(next);
    };
    dom.addEventListener(BLOCK_MENU_EVENT, onOpen);
    return () => dom.removeEventListener(BLOCK_MENU_EVENT, onOpen);
  }, [editor]);

  if (!open) return null;
  // The focus goes back first, then the menu goes.
  const close = () => {
    editor.commands.focus(undefined, { scrollIntoView: false });
    current.current?.close();
    current.current = null;
    setOpen(null);
  };
  const items: FloatingMenuItem[] = blockMenuItems(
    open.typeName,
    open.nested,
    open.own,
  ).map((item) => ({
    id: item.id,
    label: item.label,
    section: item.section,
    danger: item.danger,
    onSelect: () => {
      const target = resolveTarget(editor, open);
      if (target) item.run(editor, target);
      close();
    },
  }));
  return (
    <FloatingMenu
      anchor={open.anchor}
      placement="below"
      label={m.documents_block_menu()}
      items={items}
      onClose={close}
      className="w-52"
    />
  );
}
